import { getTimeFormat } from "@/helpers";
import type { Locales, TranslationFunctions } from "@/i18n/i18n-types";
import z from "zod";

const MicrosoftSignInOptions = z.object({
  clientId: z.string(),
  tenantId: z.string(),
  scopes: z.array(z.string())
});

const GoogleSignInOptions = z.object({
  clientId: z.string(),
  scopes: z.array(z.string())
});

const ContinuationActionValues = ['Register', 'Alert Cannot Register', 'Login With Password', 'Login With OTC'] as const

const ContinuationAction = z.enum(ContinuationActionValues)

export type ContinuationAction = z.infer<typeof ContinuationAction>

const ProfileUrlResponse = z.object({
  url: z.string().nonempty()
})

const RefreshToken = z.object({
  accessToken: z.string().nonempty(),
})

const MessageRecipientSchema = z.object({
  recipientId: z.number().gt(0),
  userId: z.number().optional(),
  studentId: z.number().optional(),
  addressInfo: z.string(),
  phoneNumber: z.string(),
  statusText: z.string().optional(),
  statusEmail: z.string().optional(),
  voiceStatusText: z.string().optional(),
  prefLanguage: z.string().optional(),
});

const ConMessagePlaceholder = z.object({
  placeholder: z.string(),
  formatNumber: z.number(),
  defaultText: z.string().optional()
})

const ConMessageRecipientVariable = z.object({
  holderNumber: z.number(),
  holderText: z.string()
})

const MessageFile = z.object({
  fileId: z.number().gt(0),
  messageId: z.number().gt(0),
  contentType: z.string(),
  fileName: z.string(),
  filePath: z.string(),
  sendDate: z.coerce.date().optional()
})
.transform(obj => ({
  ...obj,
  getFileExtension() {
    const finalDot = obj.filePath.lastIndexOf('.')
    return 0 < finalDot ? obj.filePath.slice(finalDot + 1).toLowerCase() : undefined
  }
}))
.transform(obj => ({
  ...obj,
  prettifyFileName() {
    let fileName = obj.fileName

    const finalDot = fileName.lastIndexOf('.')

    if (0 < finalDot)
      fileName = fileName.slice(0, finalDot)

    fileName = fileName.replace(/[_-]/g, ' ')
    fileName = fileName.replace(/([a-z])([A-Z])/g, '$1 $2').trim()
    fileName = fileName.replace(/\b\d{1,2}\b/g, '')

    return fileName.split(' ').map(word => {
        if (word.length === 0) return '';
        return word.charAt(0).toUpperCase() + word.slice(1);
    }).join(' ')
  },
  getFileType() {
    switch (obj.getFileExtension()) {
      case 'pdf':
        return 'pdf'
      case 'doc':
      case 'docx':
        return 'word'
      case 'ppt':
      case 'pptx':
        return 'powerpoint'
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'webp':
        return 'image'
      default:
        return 'file';
    }
  },
  getIcon() {
    switch (obj.getFileExtension()) {
      case 'pdf':
        return 'file-pdf'
      case 'doc':
      case 'docx':
        return 'file-word'
      case 'xls':
      case 'xlsx':
      case 'xlsm':
      case 'csv':
        return 'file-excel'
      case 'ppt':
      case 'pptx':
        return 'file-powerpoint'
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'webp':
        return 'file-image'
      default:
        return 'file';
    }
  }
}))

const FileListing = z.object({
  messageId: z.number().gt(0),
  messageTitle: z.string(),
  sendDate: z.coerce.date(),
  files: z.array(MessageFile)
})

export type ConMessagePlaceholder = z.infer<typeof ConMessagePlaceholder>
export type ConMessageRecipientVariable = z.infer<typeof ConMessageRecipientVariable>
export type MessageFile = z.infer<typeof MessageFile>
export type FileListing = z.infer<typeof FileListing>

const MessageSchema = z.object({
  messageId: z.number().gt(0),
  userId:  z.number().gt(0),
  userFirstName: z.string(),
  userLastName: z.string(),
  schoolId: z.number(),
  districtId: z.number().gt(0),
  title: z.string().optional(),
  schoolName: z.string().optional(),
  districtName: z.string(),
  heartCount: z.number(),
  isHearted: z.boolean(),
  allowReplies: z.boolean(),
  voiceMessageUrl: z.string().optional(),
  sendDate: z.coerce.date(),
  longSubject: z.string().optional(),
  shortContent: z.string().optional(),
  longContent: z.string().optional(),
  richSubject: z.string().optional(),
  shortFileName: z.string().optional(),
  longFileName: z.string().optional(),
  priorityLevel: z.number(),
  selected: z.string().optional(),
  selectedSenders: z.string().optional(),
  recipients: z.array(MessageRecipientSchema),
  placeholders: z.array(ConMessagePlaceholder),
  variables: z.array(ConMessageRecipientVariable)
})
.transform(obj => ({
  ...obj,
  schoolId: obj.schoolId === 0 ? undefined : obj.schoolId,
  substitutePlaceholder(content: string, placeholder: ConMessagePlaceholder, placeholderText: string) {
    const patterns = [ placeholder.placeholder, `|${placeholder.formatNumber}|`, `{${placeholder.formatNumber}}` ]

    patterns.forEach(p => {
      while (content.includes(p)) {
        content = content.replace(p, placeholderText)
      }
    })

    return content
  },
  isAlert() {
    return 0 < obj.priorityLevel
  },
  isNewsletter() {
    return obj.selectedSenders?.includes('rich')
  }
}))
.transform(obj => ({
  ...obj,
  userFullName() {
    return `${obj.userFirstName} ${obj.userLastName}`
  },
  formatContent(content?: string) {
    if (!content)
      return undefined

    let formattedContent = content

    obj.placeholders.forEach(placeholder => {
      if (placeholder.formatNumber === 0 && obj.schoolName) {
        formattedContent = obj.substitutePlaceholder(formattedContent, placeholder, obj.schoolName)
      }
      else {
        const variable = obj.variables.find(v => v.holderNumber === placeholder.formatNumber)
        formattedContent = obj.substitutePlaceholder(formattedContent, placeholder, variable?.holderText ?? placeholder.defaultText ?? '')
      }
    })

    return formattedContent
  },
  stripFontColors(content?: string) {
    if (!content)
      return undefined

    const pattern = /(color: #000000;|background-color: transparent;)/g

    return content.replace(pattern, '')
  },
  formatTimeAgo(locale: Locales) {
    return getTimeFormat(obj.sendDate, locale)
  }
})).transform(obj => ({
  ...obj,
  formatTitle() {
    return obj.formatContent(obj.title)
  },
  formatLongSubject() {
    return obj.stripFontColors(obj.formatContent(obj.longSubject))
  },
  formatShortContent() {
    return obj.stripFontColors(obj.formatContent(obj.shortContent))
  },
  formatLongContent() {
    return obj.stripFontColors(obj.formatContent(obj.longContent))
  },
  formatVoiceContent() {
    if (obj.voiceMessageUrl)
      return `<audio class='_width:100%!' controls src='${obj.voiceMessageUrl}'>Your browser does not support the audio element.</audio>`
  }
}))

const MemberSession = z.object({
  sessionId: z.number().gt(0),
  lastActive: z.coerce.date(),
  ipAddress: z.string().optional(),
  browser: z.string().optional(),
  operatingSystem: z.string().optional(),
  location: z.string().optional(),
  authenticationType: z.string().optional(),
})

export type PostMessage = z.infer<typeof MessageSchema>;
export type MemberSession = z.infer<typeof MemberSession>;
export type RefreshToken = z.infer<typeof RefreshToken>;
export type GoogleSignInOptions = z.infer<typeof GoogleSignInOptions>;
export type MicrosoftSignInOptions = z.infer<typeof MicrosoftSignInOptions>;

const GetThreadAttachmentUrlResponse = z.object({
  url: z.string().nonempty()
})

const GetMemberHasPasswordResponse = z.object({
  hasPassword: z.boolean()
})

const RegistrationDefaultsResponse = z.object({
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  language: z.string(),
  phoneNumber: z.string().optional(),
})

export type RegistrationDefaultsResponse = z.infer<typeof RegistrationDefaultsResponse>

const EventFile = z.object({
  fileUrl: z.string().optional(),
  title: z.string().optional()
})

const EventSource = z.object({
  title: z.string().optional(),
  url: z.string().optional()
})

const EventDateTime = z.object({
  dateTime: z.coerce.date().optional(),
  date: z
    .string()
    .optional()
    .transform(val => {
      if (!val) return null
      const [y, m, d] = val.split('-').map(Number)
      return new Date(y, m - 1, d, 0, 0, 0)
    }),
  timeZone: z.string().optional()
})

const EventPersonInfo = z.object({
  email: z.string(),
  displayName: z.string()
})

const EventInfo = z.object({
  id: z.string(),
  calendarId: z.string(),
  summary: z.string().optional(),
  description: z.string().optional(),
  location: z.string().optional(),
  status: z.string(),
  anyoneCanAddSelf: z.boolean(),
  htmlLink: z.string().optional(),
  start: EventDateTime.optional(),
  end: EventDateTime.optional(),
  organizer: EventPersonInfo,
  colorId: z.string().optional(),
  colorHex: z.string().optional(),
  recurringEventId: z.string().optional(),
  endTimeUnspecified: z.boolean().optional(),
  source: EventSource.optional(),
  eventType: z.string().optional(),
  attachments: z.array(EventFile).optional()
}).transform(obj => ({
  ...obj,
  formatEventDuration(translation: TranslationFunctions) {
    const start = obj.start?.dateTime ?? obj.start?.date
    const end = obj.end?.dateTime ?? obj.end?.date

    if (!start || !end) return translation.ALL_DAY()

    const startDate = new Date(start)
    const endDate = new Date(end)

    if (obj.start?.date && obj.end?.date) return translation.ALL_DAY()

    const diffMs = endDate.getTime() - startDate.getTime()
    if (diffMs <= 0) return translation.ALL_DAY()

    const hours = Math.floor(diffMs / 36e5)
    const minutes = Math.floor((diffMs % 36e5) / 60000)

    if (hours === 0 && minutes === 0) return translation.ALL_DAY()
    if (hours === 0) return `${minutes} ${translation.MINUTE_ABBREVIATION()}`
    if (minutes === 0) return `${hours} ${translation.HOUR_ABBREVIATION()}`
    return `${hours} ${translation.HOUR_ABBREVIATION()} ${minutes} ${translation.MINUTE_ABBREVIATION()}`
  },
  formatEventStartTime(locale: Locales = 'en') {
    return obj.start?.dateTime != null ? obj.start.dateTime.toLocaleTimeString(locale, {hour:'numeric', minute:'2-digit', hour12: true}) : ''
  },
  formatEventEndTime(locale: Locales = 'en') {
    return obj.end?.dateTime != null ? obj.end.dateTime.toLocaleTimeString(locale, {hour:'numeric', minute:'2-digit', hour12: true}) : ''
  }
}))

const CalendarInfo = z.object({
  id: z.string(),
  timeZone: z.string(),
  items: z.array(EventInfo)
})

export type EventFile = z.infer<typeof EventFile>
export type EventSource = z.infer<typeof EventSource>
export type EventDateTime = z.infer<typeof EventDateTime>
export type EventPersonInfo = z.infer<typeof EventPersonInfo>
export type EventInfo = z.infer<typeof EventInfo>
export type CalendarInfo = z.infer<typeof CalendarInfo>

const Group = z.object({
  id: z.number().gt(0),
  schoolId: z.number(),
  districtId: z.number(),
  name: z.string(),
  description: z.string().optional(),
  created: z.coerce.date(),
  canOptIn: z.boolean(),
  canOptOut: z.boolean(),
  members: z.array(z.object({
    id: z.number().gt(0),
    firstName: z.string(),
    lastName: z.string(),
    optedIn: z.boolean()
  }).transform(obj => ({
    ...obj,
    fullName() {
      return `${obj.firstName} ${obj.lastName}`
    }
  }))),
  staffMembers: z.array(z.object({
    id: z.number().gt(0),
    firstName: z.string(),
    lastName: z.string(),
    position: z.string().optional()
  }).transform(obj => ({
    ...obj,
    fullName() {
      return `${obj.firstName} ${obj.lastName}`
    }
  }))),
  district: z.object({
    id: z.number().gt(0),
    name: z.string()
  }).optional(),
  school: z.object({
    id: z.number().gt(0),
    name: z.string()
  }).optional()
}).transform(obj => ({
  ...obj,
  isMember(memberId: number) {
    return obj.members.some(m => m.id === memberId)
  }
}))

export type Group = z.infer<typeof Group>

export const zodSchema = {
  MessageSchema,
  MessageFile,
  FileListing,
  MicrosoftSignInOptions,
  GoogleSignInOptions,
  ProfileUrlResponse,
  RefreshToken,
  Group,
  MemberSession,
  GetThreadAttachmentUrlResponse,
  ContinuationAction,
  GetMemberHasPasswordResponse,
  RegistrationDefaultsResponse,
  EventFile,
  EventSource,
  EventDateTime,
  EventPersonInfo,
  EventInfo,
  CalendarInfo
}
